import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { desktop_small, mobile_xl, mobile_xs } from '../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, FlexCenter, LineClamp } from '../../global/commonStyle';

interface Props {
  isHide?: boolean;
  isOverdue?: boolean;
  showRightBorder?: boolean;
  isTaskItem?: boolean;
  color?: string;
  isActive?: boolean;
  isMainTask?: boolean;
  isRecurringTask?: boolean;
  isMember?: boolean;
  isTemplateTaskPreview?: boolean;
  isEmptyState?: boolean;
}

const DropdownWrapper = styled.div`
  display: flex;
`;

const Group = styled.div<Props>`
  margin: auto;
  padding-inline: var(--space-24);
  ${({ isEmptyState, isMember }) =>
    isEmptyState &&
    css`
      height: calc(100dvh - 47px);
      position: relative;
      ${mobile_xl(css`
        height: ${isMember ? 'calc(100dvh - 122px)' : 'calc(100dvh - 80px)'};
      `)}
    `}
  ${mobile_xl(css`
    padding-inline: var(--space-20);
  `)}
`;

const Heading = styled.div`
  ${AlignCenter}
`;

const Title = styled.h1`
  ${Typography.heading_md};
  color: var(--text-primary);
  margin-block: var(--space-2);
  white-space: nowrap;
  padding-block: var(--space-1);
`;

const Menu = styled.div`
  ${AlignCenter}
  gap: var(--space-8);
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: var(--space-10) var(--space-24);
  border-bottom: 0.5px solid var(--border-primary);
  background: var(--background-secondary);
  position: sticky;
  top: 0;
  z-index: var(--z-index-999);
`;

const ProjectName = styled.div<Props>`
  ${(props: Props) =>
    props.showRightBorder &&
    css`
      margin-right: var(--space-12);
      padding-right: var(--space-12);
      border-right: 0.5px solid var(--border-primary);
      ${props.isRecurringTask &&
      css`
        border: none;
        margin-right: var(--space-0);
        padding-right: var(--space-0);
      `}
    `}
  p {
    margin: var(--space-0);
    ${Typography.body_md}
    color: var(--text-secondary);
    cursor: pointer;
    padding: var(--space-4) var(--space-8);
    white-space: nowrap;

    &:hover {
      background-color: var(--neutral-secondary);
      border-radius: var(--border-radius-full);
    }
  }
  ${mobile_xl(css`
    padding-right: var(--space-8);
    p {
      padding: var(--space-0);
    }
  `)}
`;

const Task = styled.div<Props>`
  margin-block: var(--space-20);
  ${mobile_xl(css`
    margin-block: var(--space-0) var(--space-24);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  `)}
  ${(props) =>
    props.isMember &&
    css`
      &:first-child {
        margin-top: var(--space-24);
      }
      ${mobile_xl(css`
        &:first-child {
          margin-top: var(--space-0);
        }
      `)}
    `}
`;

const Deadline = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
  svg {
    vertical-align: middle;
  }
`;

const Form = styled.div<Props>`
  ${AlignCenter}
  gap: ${(props: Props) => (props.isTaskItem ? 'var(--space-6)' : 'var(--space-10)')};
  ${mobile_xl(css`
    gap: var(--space-6);
  `)}
`;

const ButtonIcon = styled.div`
  padding: var(--space-4);
  border-radius: var(--border-radius-12);
  display: flex;
  &:hover {
    background-color: var(--neutral-secondary);
    input {
      filter: brightness(var(--brightness-value));
    }
  }
`;

const Label = styled.label<Props>`
  width: auto;
  max-width: 920px;
  ${Typography.body_md};
  color: var(--text-primary);
  margin: var(--space-0);
  ${LineClamp(1)}
  word-break: break-all;
  white-space: pre-wrap;
  a {
    color: var(--text-link);
    text-decoration: underline;
    display: inline-block;
    &:hover {
      text-decoration: none;
    }
  }
  ${mobile_xl(css<Props>`
    max-width: ${({ isTemplateTaskPreview }) => (isTemplateTaskPreview ? 'unset' : '150px')};
    ${Typography.body_md_medium};
  `)}
  ${mobile_xs(css`
    max-width: 90px;
  `)}
  ${(props) =>
    props.isMainTask &&
    css`
      ${mobile_xl(css`
        max-width: 100%;
      `)}
    `}
`;

const TaskContent = styled.div<Props>`
  position: relative;
  width: 100%;
  ${FlexBetween}
  border: 0.5px solid transparent;
  padding: var(--space-3) var(--space-5);
  margin-inline: -10px;
  border-radius: var(--border-radius-full);
  gap: var(--space-40);
  &:hover {
    background: var(--background-primary);
    border: 0.5px solid var(--border-primary);
    box-shadow: var(--shadow-card);

    .svg-icon {
      opacity: 1;
    }
    .plus-icon {
      visibility: visible;
    }
    .task-input {
      background-color: var(--text-white);
    }
  }

  ${(props: Props) =>
    props.isActive &&
    css`
      background: var(--background-primary);
      border: 0.5px solid var(--border-primary);
      box-shadow: var(--shadow-card);
    `}

  .resp-assign {
    display: none;
  }
  ${mobile_xl(css`
    align-items: center;
    gap: var(--space-6);
    border: none;
    padding: var(--space-10) var(--space-0);
    margin: var(--space-0);
    .resp-assign {
      display: block;
    }
    .assign {
      display: none;
    }
    .webdropdown {
      display: none;
    }
    &:hover {
      border: none;
      box-shadow: none;
      background: none;
      svg-icon {
        opacity: 1;
      }
    }
    .calendar-icon {
      visibility: visible;
    }
    &.task-item {
      padding: var(--space-10) var(--space-0);
      width: calc(100vw - 36px);
      margin-inline: -4px;
    }
  `)}
`;

const TaskItem = styled.div`
  ${AlignCenter}
  gap: var(--space-16);
  ${mobile_xl(css`
    ${FlexBetween}
    width: 100%;
  `)}
`;

const AssignGroup = styled.div`
  ul {
    ${AlignCenter}
    flex-wrap: wrap;
    list-style-type: none;
    padding: var(--space-0);
    margin: var(--space-0);
    width: max-content;
  }

  li {
    margin-left: -10px;
    border-radius: var(--border-radius-full);
    background-color: var(--background-muted);
    ${mobile_xl(css`
      :first-child {
        margin-left: var(--space-0);
      }
    `)}
  }

  .blank-plus-icon {
    z-index: var(--z-index-base);
    width: 20px;
    height: 20px;
    ${FlexCenter}
    border: 1px solid var(--background-secondary);
  }

  .plus-icon {
    visibility: hidden;
    z-index: var(--z-index-base);
    width: 20px;
    height: 20px;
    ${FlexCenter}
    border: 1px solid var(--background-secondary);
  }
`;

const Text = styled.p<Props>`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  white-space: nowrap;

  ${(props: Props) =>
    props.isOverdue &&
    css`
      color: var(--accent-error);
    `}
  ${mobile_xl(css`
    margin-left: -2px;
  `)}
`;

const ProjectTitle = styled.h1`
  ${Typography.heading_md};
  color: var(--text-primary);
  margin: var(--space-0);
  padding-bottom: var(--space-6);
  ${mobile_xl(css`
    padding: var(--space-0);
  `)}
`;

const AvatarImage = styled.div`
  ${AlignCenter}
  border-radius: var(--border-radius-full);

  > div > img {
    max-width: 100%;
  }
  ${mobile_xl(css`
    .avtar {
      width: 22px;
      height: 22px;
      border: 1px solid var(--background-primary);
    }
  `)}
`;

const Tasklist = styled.div<Props>`
  ${FlexBetween}

  ${(props: Props) =>
    props.isHide &&
    css`
      display: none;
    `}
`;

const TaskIcons = styled.div`
  display: flex;
  gap: var(--space-12);
  position: relative;
`;

const Filter = styled.div<Props>`
  cursor: pointer;
  ${AlignCenter}
  gap: var(--space-4);
  padding: var(--space-5) var(--space-10) var(--space-5) var(--space-8);
  background: var(--background-primary);
  border-radius: var(--border-radius-8);
  box-shadow: var(--border);

  &:hover {
    background-color: var(--background-secondary);
    box-shadow: var(--button-border-hover);
  }
  .filters-icon-color {
    fill: var(--text-secondary);
  }
  ${(props: Props) =>
    props.isHide &&
    css`
      border: none;
      background: none;
      box-shadow: none;
      padding: var(--space-6);
      &:hover {
        background: var(--neutral-secondary);
        border-radius: var(--border-radius-8);
      }
    `}
  ${mobile_xl(css`
    background: unset;
    border: unset;
    border-radius: var(--border-radius-full);
    box-shadow: unset;
    padding: var(--space-6);
    &:hover {
      background-color: unset;
      box-shadow: unset;
    }
    svg {
      width: 20px;
      height: 20px;
    }
  `)}
`;

const SelectText = styled.div<Props>`
  ${Typography.body_sm_medium}
  color: var(--text-primary);
  margin: var(--space-0);
  ${(props: Props) =>
    props.isHide &&
    css`
      display: none;
    `}
  ${mobile_xl(css`
    display: none;
  `)}
`;

const DueDate = styled.div`
  cursor: default;
  ${FlexCenter}
  gap: var(--space-4);
  span {
    ${AlignCenter}
    gap: var(--space-4);
  }
`;
const ProjectDate = styled.div`
  ${FlexCenter}
  ${mobile_xl(css`
    display: none;
  `)}
`;

const TaskTitle = styled.div`
  width: 100%;
  ${AlignCenter}
  gap: var(--space-6);
  ${mobile_xl(css`
    padding-block: var(--space-2);
  `)}
`;
const MainTaskTitle = styled.div`
  ${Typography.body_md}
  color: var(--text-secondary);
  ${LineClamp(1)}
  word-break: break-all;
  a {
    color: var(--text-link);
    text-decoration: underline;
    display: inline;
    &:hover {
      text-decoration: none;
    }
  }
  @media (max-width: 1086px) {
    max-width: 120px;
  }
  ${desktop_small(css`
    max-width: 80px;
  `)}
  ${mobile_xl(css`
    max-width: 150px;
    ${Typography.body_md_medium};
  `)}
  ${mobile_xs(css`
    max-width: 90px;
  `)}
`;
const DropBlock = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  padding: var(--space-2) var(--space-10) var(--space-2) var(--space-6);
  border-left: 0.5px solid var(--border-primary);
  &.left-border {
    border-left: none;
  }
`;
const DotIcon = styled.div<Props>`
  margin: var(--space-4);
  width: 8px;
  height: 8px;
  border-radius: var(--border-radius-full);
  ${(props: Props) =>
    props.color &&
    css`
      background-color: ${props.color};
    `}
`;
const DropLabel = styled.div`
  ${Typography.body_md}
  color: var(--text-primary);
  white-space: nowrap;
`;

const MultipleLabel = styled.div`
  border-radius: var(--border-radius-full);
  border: 0.5px solid var(--border-primary);
  display: flex;
  ${mobile_xl(css`
    display: none;
  `)}
`;

const MainTooltipDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const IconDueDate = styled.div<Props>`
  .fill-color {
    width: 16px;
    height: 16px;
    fill: var(--text-secondary);
  }

  &:hover .fill-color {
    fill: var(--text-primary);
  }
  &:active .fill-color {
    fill: var(--text-primary);
  }
  .calendar-fill-color {
    fill: var(--text-secondary);
  }
  .calendar-due-fill-color {
    fill: var(--accent-error);
  }
  display: flex;
  p {
    ${Typography.body_md}
    color: var(--text-primary);
    white-space: nowrap;
    margin: var(--space-0);
    h6 {
      ${Typography.body_md}
      color: var(--text-secondary);
      white-space: nowrap;
      margin: var(--space-0);
      &:hover {
        color: var(--text-primary);
      }
    }
  }

  .task-grid-calander-icon {
    display: none;
    padding: var(--space-3);
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-4);
    width: 16px;
    height: 16px;
    path {
      fill: var(--text-secondary);
    }
  }
  .task-grid-calander-icon:hover {
    background-color: var(--neutral-secondary);
    border: 0.5px solid transparent;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  gap: var(--space-24);
`;
const Div = styled.div`
  ${mobile_xl(css`
    margin-block: var(--space-8) var(--space-96);
  `)}
`;

const TooltipWrapper = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
`;

const SVGDiv = styled.div`
  ${FlexCenter}
`;

const TooltipDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
`;

export {
  DropdownWrapper,
  Group,
  Heading,
  Title,
  Menu,
  Text,
  Label,
  TaskHeader,
  ProjectName,
  Task,
  Deadline,
  Form,
  TaskContent,
  TaskItem,
  ProjectTitle,
  AssignGroup,
  Tasklist,
  AvatarImage,
  TaskIcons,
  Filter,
  DueDate,
  ProjectDate,
  SelectText,
  TaskTitle,
  MainTaskTitle,
  DotIcon,
  DropLabel,
  DropBlock,
  MultipleLabel,
  IconDueDate,
  HeaderTitle,
  Div,
  ButtonIcon,
  TooltipWrapper,
  SVGDiv,
  TooltipDiv,
  MainTooltipDiv
};
